// The vocabulary a QC Flow rule check is written and read in: the metrics a check can be built
// on, and the value dictionaries each metric family draws its threshold from.
//
// Promoted out of ProjectIdSuiteComponent for NK-322, where the Quality pillar's DAR messages
// have to describe a removal by the rule check that caused it. Two screens now depend on these
// lists, so they live here and a check is rendered to text in one place only - see
// `helpers/qc-rule-check`. Reading a value dictionary directly to build your own wording puts
// the same rule on screen described two different ways.

export interface QcRuleMetric {
  key: string;
  label: string;
}

// The subjects a check can be built on, in the order the rule builder offers them.
export const QC_RULE_METRICS: QcRuleMetric[] = [
  { key: 'AI', label: 'AI advice' },
  { key: 'IsSynthetic', label: 'Synthetic' },
  { key: 'AI_Context', label: 'AI - Context' },
  { key: 'AI_Content', label: 'AI - Content' },
  { key: 'AI_Vulgarity', label: 'AI - Vulgarity' },
  { key: 'AI_Generated', label: 'AI - Generation' },
  { key: 'Flag', label: 'Flag' },
  { key: 'My', label: 'My assessment' },
  { key: 'RID', label: 'RID Result' },
  { key: 'RID_CPS', label: 'RID Result - CPS' },
  { key: 'RID_Summary', label: 'RID Summary' },
  { key: 'Blank', label: 'Blanks' },
  { key: 'NonAnswer', label: 'Non-Answers' },
  { key: 'DupeAnswer', label: 'Repeated in own answers' },
  { key: 'DupeRespondent', label: 'Shared with other respondents' },
];

// Metrics whose threshold is a graded value, read as "<metric> is <value>". Which dictionary the
// value comes from depends on the metric: RID reads High / Medium / Low, ResponseID reads its
// combined rating-and-recommendation words, Synthetic reads True / False, and the rest read the
// risk words.
export const QC_RULE_RISK_METRICS = ['AI', 'AI_Context', 'AI_Content', 'AI_Vulgarity', 'AI_Generated', 'My', 'RID', 'RID_Summary', 'IsSynthetic'];

// Metrics read at respondent level, or across every question at once, so a check on them has no
// question scope to pick.
export const QC_RULE_NO_QUESTION_METRICS = ['IsSynthetic', 'Blank', 'NonAnswer', 'DupeAnswer', 'RID_Summary'];

// Metrics whose threshold is an operator and a number, read as "<metric> count is <operator>
// <value>". RID_CPS reads "value is" rather than "count is" - it is a score, not a tally of
// questions.
export const QC_RULE_OPERATOR_METRICS = ['DupeRespondent', 'DupeAnswer', 'Blank', 'NonAnswer', 'RID_CPS'];

// How an operator metric's threshold reads: "value is" for a measured value, "count is" for
// everything else. Shared by the rule builder and the rendered rule text.
export const QC_RULE_VALUE_METRICS = ['RID_CPS'];

// The single metric read as "<metric> has <value>".
export const QC_RULE_FLAG_METRIC = 'Flag';

// Risk grades as the rule builder words them. The stored value is the severity, never the word,
// so a wording change here does not touch a stored rule.
export const QC_RULE_RISK_VALUES: Record<string, string> = {
  'high': 'Bad',
  'medium': 'Warn',
  'low': 'Good',
};

// AI - Generation shares the risk severities but is worded as a detection, not a verdict: the
// question a researcher is asking of that score is whether the answer was AI-written, and "Good"
// reads as praise for the answer rather than an absence of AI. Display only - a rule still stores
// high / medium / low, and QC_RULE_AI_GENERATED_METRIC is the one metric that reads them this way.
export const QC_RULE_AI_GENERATED_METRIC = 'AI_Generated';

export const QC_RULE_AI_GENERATED_VALUES: Record<string, string> = {
  'high': 'Detected',
  'medium': 'Unsure',
  'low': 'Not detected',
};

// RID grades share the same stored severities as risk but are worded as a level, not a verdict.
export const QC_RULE_RID_VALUES: Record<string, string> = {
  'high': 'High',
  'medium': 'Medium',
  'low': 'Low',
};

// ResponseID is the respondent-level verdict: the rating, with High split by the recommendation
// that goes with it. ResponseID's recommendation follows its rating - Low is always None and
// Medium always Review - so only High needs the recommendation to tell a plain High (review) from
// a hard stop (auto-QC, its strongest call: a bot or automation flag). Listed mildest first, the
// order the rule builder and the column filter show them in.
export const QC_RULE_RID_SUMMARY_VALUES: Record<string, string> = {
  'low': 'Low',
  'medium': 'Medium',
  'high-review': 'High - Review',
  'high-auto-qc': 'High - Auto-QC',
};

// Synthetic is a boolean stored as text.
export const QC_RULE_SYNTHETIC_VALUES: Record<string, string> = {
  'true': 'True',
  'false': 'False',
};

// A flag check's value arrives quoted, because it is compared against the flag column as written
// in the file rather than as a number.
export const QC_RULE_FLAG_VALUES: Record<string, string> = {
  "'0'": 'Not failed',
  "'1'": 'Failed',
};

export const QC_RULE_OPERATORS: Record<string, string> = {
  '==': 'Equal to',
  '>': 'Greater than',
  '<': 'Less than',
};
